/// <amd-module name="Wiki/Search/RecentWikiPagesProvider" />
import * as React from "react";
import { ITfsPageService, IProjectData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IInstantSearchMenuService, IMenuServiceContext } from "InstantSearch/Shared/Types";
import { IPrefetchInstantSearchContentService } from "Wit/Search/PrefetchContent/PrefetchInstantSearchContentService";
import { IRecentActivity } from "Wiki/Search/Types";
import { CustomerIntelligenceConstants } from "Wiki/Search/Constants";
import * as Resources from "Wiki/Search/Resources";

const maxResultRows = 5;

/**
 * Service for creating menu items for recent wiki pages.
 */
export class RecentWikiPagesProvider extends BaseInstantSearchProvider<IRecentActivity[] | undefined> implements IInstantSearchMenuService<IMenuServiceContext> {
    private _contributionId = "ms.vss-wiki-web.recent-wikipages-project-menu-provider";
    private _projectProperties: IProjectData | undefined;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && !searchText) {
            this.getSuggestions(
                "",
                context,
                "CachedRecentWikiPagesFetched",
                CustomerIntelligenceConstants.TtiSearchScenarioName,
                CustomerIntelligenceConstants.SubSequentSearchScenarioName
            );
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<any[] | undefined> {
        const prefetchInstantSearchContentService = this.pageContext
            .getService<IPrefetchInstantSearchContentService>("IPrefetchInstantSearchContentService");
        return prefetchInstantSearchContentService.recentWikiPagesCache;
    }

    protected _onFetchSuccess(searchText: string, context: IMenuServiceContext, data: IRecentActivity[] | undefined): void {
        const { searchInput, telemetryWriter, onFocus, invokedProvidersProps } = context;
        this._menuItems.splice(0, this._menuItems.length);

        if (this.shouldInvokeProvider(context) && searchInput.queryText().trim()) {
            // Search text is non-empty. It got changed before fetching results. So, not updating menuItems.
            return;
        }

        if (data && data.length > 0) {
            const header = invokedProvidersProps && invokedProvidersProps.props
                ? invokedProvidersProps.props.emptyTextHeader
                : Resources.RecentWikiPagesHeader;
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            const limitOfWikiPages = Math.min(maxResultRows, data.length);
            for (let index = 0; index < limitOfWikiPages; index++) {
                const wikiPage = data[index];
                const artifactIdSplitted = wikiPage.artifactId.split("/");
                const wikiId = artifactIdSplitted[0];
                const wikiPageId = artifactIdSplitted[1];
                const pagePath = JSON.parse(wikiPage.activityDetails).PagePath;
                const pagePathSplitted = pagePath.split("/");
                const title = pagePathSplitted[pagePathSplitted.length - 1];

                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow
                                    iconProps={{ iconName: 'TextDocument' }}
                                    title={title}
                                />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                        this._navigateToWikiPage(
                            this.projectProperties ? this.projectProperties.id : "",
                            wikiId,
                            wikiPageId,
                            event,
                            event && (event.ctrlKey || event.metaKey),
                            () => this._recordWikiPageClickTelemetry(menuItem.data, telemetryWriter))
                });
            }

            announce(Resources.RecentWikiPageLoadedMessage);
        } else if ((data && data.length === 0) || !data) {
            telemetryWriter.publish("NoSuggestionsFound", { "type": "RecentWikiPages" });
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: Error): void {
        const { invokedProvidersProps } = context;
        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.emptyTextHeader
            : Resources.RecentWikiPagesHeader;
        this._menuItems.splice(0, this._menuItems.length);
        this._menuItems.push(
            {
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            },
            {
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: Resources.GenericErrorMessage
            }
        );

        announce(Resources.GenericErrorMessage);
    }

    private _recordWikiPageClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "RecentWikiPage"
        });
    }


    private _navigateToWikiPage = (
        projectId: string,
        wikiId: string,
        wikiPageId: string,
        evt: React.KeyboardEvent<HTMLElement>,
        openInNewTab?: boolean,
        callback?: () => void
    ): void => {
        if (openInNewTab) {
            const url = this._getPageUrl(projectId, wikiId, wikiPageId)
            const newWindow = window.open(url, "_blank");
            if (newWindow && newWindow.opener) {
                newWindow.opener = null;
            }
        }
        else {
            onClickFPS(this.pageContext, this._getPageUrl(projectId, wikiId, wikiPageId), true, evt);
        }

        if (callback) {
            callback();
        }
    }

    private _getPageUrl = (projectId: string, wikiId: string, wikiPageId: string): string => {

        const routeValues: { [key: string]: string } = {
            wikiIdentifier: wikiId,
            pageId: wikiPageId,
            project: projectId
        };

        return routeUrl(this.pageContext, "ms.vss-wiki-web.wiki-overview-nwp-route2", routeValues);
    }

    private get projectProperties(): IProjectData | undefined {
        if (!this._projectProperties) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._projectProperties = tfsPageData ? tfsPageData.project : undefined;
        }

        return this._projectProperties;
    }
}

Services.add(
    "project-recent-wikipages-provider-service",
    { serviceFactory: RecentWikiPagesProvider }
);
